import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAiJobs } from "@/lib/ai-jobs";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Film, Play, Loader2, Trash2, RefreshCw, AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const STEP_LABELS = {
    queued: "Queued…",
    rendering_slide: "Rendering slide",
    stitching: "Stitching final video",
};

// OpenAI TTS voices the backend accepts. The "feel" labels are our own
// editorial — kept short so the dropdown is scannable. If we add more
// voices server-side, append them here.
const VOICE_OPTIONS = [
    { value: "nova", label: "Nova", feel: "warm, friendly (default)" },
    { value: "alloy", label: "Alloy", feel: "neutral, professional" },
    { value: "echo", label: "Echo", feel: "calm, measured" },
    { value: "fable", label: "Fable", feel: "expressive, storyteller" },
    { value: "onyx", label: "Onyx", feel: "deep, authoritative" },
    { value: "shimmer", label: "Shimmer", feel: "bright, upbeat" },
    { value: "ash", label: "Ash", feel: "thoughtful, mellow" },
    { value: "verse", label: "Verse", feel: "rhythmic, dynamic" },
];

const DEFAULT_VOICE = "nova";

/**
 * Renders the "Convert storyboard to MP4" workflow inside the LessonDialog.
 *
 * Props:
 *  - lessonId: required — string. We poll the job status for this lesson.
 *  - videoUrl: optional — current `lesson.video_url` (e.g. "/api/files/public/{id}")
 *  - hasStoryboard: boolean — whether the lesson has a video_storyboard
 *  - onChanged: () => void — called after a successful render or delete (so parent can refresh)
 *  - onBeforeRender: optional async () => boolean — runs right before the
 *    render API call. If provided, gives the parent dialog a chance to
 *    save unsaved form changes (esp. a freshly-attached storyboard) so
 *    the backend sees the latest data. Return `false` to abort the render.
 */
export default function LessonVideoRenderPanel({
    lessonId,
    lessonTitle,
    chapterTitle,
    videoUrl,
    hasStoryboard,
    onChanged,
    onBeforeRender,
}) {
    const { trackJob, jobs: trayJobs } = useAiJobs();
    const [jobId, setJobId] = useState(null);
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    // Local cache of the freshly-rendered URL. Used as a fallback when
    // the parent's `videoUrl` prop hasn't refreshed yet (e.g. the
    // CurriculumBuilder above us only updates its lesson list on
    // `onSaved`, but the `lesson` prop reference passed into this
    // dialog isn't re-keyed — so the form never sees the new url).
    // Cleared when the parent's prop catches up so we don't show a
    // stale URL after the user navigates away and back.
    const [localVideoUrl, setLocalVideoUrl] = useState(null);
    // User-selected voice for the next render. Persists across re-renders
    // within the same dialog session so the user doesn't have to re-pick
    // every time they iterate on a script.
    const [voice, setVoice] = useState(DEFAULT_VOICE);
    const pollRef = useRef(null);

    // Always use a same-origin URL for media elements to avoid cross-origin /
    // CSP issues (which break <video> on misconfigured production deploys).
    // Prefer the parent's prop, falling back to our locally-cached URL.
    const fullVideoUrl = videoUrl || localVideoUrl || null;

    // When the parent's prop catches up to our local cache, clear the
    // local override so we don't keep "remembering" old URLs.
    useEffect(() => {
        if (videoUrl && localVideoUrl && videoUrl === localVideoUrl) {
            setLocalVideoUrl(null);
        }
    }, [videoUrl, localVideoUrl]);

    useEffect(() => {
        if (!jobId) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await api.get(`/lesson-video-jobs/${jobId}`);
                if (cancelled) return;
                setJob(data);
                if (data.status === "done") {
                    setBusy(false);
                    setJobId(null);
                    toast.success("Lesson video ready");
                    // Defensive: stash the rendered URL locally so we
                    // can flip to <video> mode immediately even if the
                    // parent dialog never refreshes its `videoUrl`
                    // prop. The job's `video_url` is set inside the
                    // same DB update that flipped status to done, so
                    // it must exist by the time we hit this branch.
                    if (data.video_url) {
                        setLocalVideoUrl(data.video_url);
                    }
                    onChanged?.(data.video_url);
                } else if (data.status === "failed") {
                    setBusy(false);
                    setJobId(null);
                    toast.error(`Render failed: ${data.error || "unknown error"}`);
                }
            } catch (e) {
                if (!cancelled) {
                    setBusy(false);
                    setJobId(null);
                }
            }
        };
        tick();
        pollRef.current = window.setInterval(tick, 2200);
        return () => {
            cancelled = true;
            if (pollRef.current) window.clearInterval(pollRef.current);
        };
    }, [jobId, onChanged]);

    const startRender = async () => {
        if (!lessonId || !hasStoryboard) return;
        setBusy(true);
        setJob(null);
        try {
            // Give the parent dialog a chance to save unsaved form
            // changes (especially a freshly-attached storyboard) so the
            // server sees the latest data before we kick off the render.
            // Without this, users who just clicked "Convert to video"
            // and immediately scroll down to Render hit a 400 "No
            // storyboard attached" because the form state is ahead of
            // the database.
            if (onBeforeRender) {
                const ok = await onBeforeRender();
                if (ok === false) {
                    setBusy(false);
                    return;
                }
            }
            const { data } = await api.post(`/lessons/${lessonId}/render-video`, {
                voice,
            });
            setJobId(data.job_id);
            setJob({ status: "queued", current: 0, total: data.total, step: "queued" });
            // Also hand off to the global AI-jobs tray so progress remains
            // visible (and the render keeps polling) even if the user
            // closes the lesson dialog or jumps to a different lesson.
            trackJob({
                kind: "video",
                jobId: data.job_id,
                lessonId,
                lessonTitle: data.lesson_title || lessonTitle || "Lesson",
                chapterTitle: data.chapter_title || chapterTitle || "Chapter",
                total: data.total,
                onDone: (jobData) => {
                    // Broadcast so the chapter view refreshes its
                    // lesson list (video badge / new URL).
                    try {
                        window.dispatchEvent(new CustomEvent("cco:lesson-video-rendered", {
                            detail: { lessonId, videoUrl: jobData?.video_url },
                        }));
                    } catch { /* no-op */ }
                },
            });
        } catch (e) {
            setBusy(false);
            toast.error(e?.response?.data?.detail || "Could not start video render.");
        }
    };

    // If the user reopens a lesson dialog while a render for THIS lesson is
    // still active in the global tray, hydrate our local state from the
    // tray so the in-dialog progress bar resumes seamlessly instead of
    // showing the "Render video" CTA. Without this the user would see the
    // CTA, click it, and start a duplicate render.
    useEffect(() => {
        if (!lessonId || busy || jobId) return;
        const trayJob = Object.values(trayJobs || {}).find(
            (j) => j.kind === "video" && j.lessonId === lessonId
                && (j.status === "running" || j.status === "queued"),
        );
        if (trayJob) {
            setJobId(trayJob.jobId);
            setJob({
                status: trayJob.status,
                step: trayJob.step || "queued",
                current: trayJob.current || 0,
                total: trayJob.total || 1,
            });
            setBusy(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonId, trayJobs]);

    const removeVideo = async () => {
        if (!lessonId) return;
        try {
            await api.delete(`/lessons/${lessonId}/video`);
            toast.success("Video removed");
            // Clear our defensive cache so the CTA reappears.
            setLocalVideoUrl(null);
            // Empty string tells the parent to clear `form.video_url`
            // — the panel then drops back to the "Render video" CTA.
            onChanged?.("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not remove video.");
        }
    };

    if (!lessonId) {
        // New (unsaved) lesson — show inline hint
        if (hasStoryboard) {
            return (
                <div
                    className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground flex items-start gap-2"
                    data-testid="video-render-save-hint"
                >
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    Save the lesson first — once saved, you can render the storyboard into a
                    narrated MP4 from this panel.
                </div>
            );
        }
        return null;
    }

    // Existing lesson, no storyboard yet
    if (!hasStoryboard && !fullVideoUrl) {
        return (
            <div
                className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2"
                data-testid="video-render-no-storyboard"
            >
                <Film className="h-3.5 w-3.5" />
                Convert this lesson to a storyboard first (using the AI button above) to enable
                video render.
            </div>
        );
    }

    // In-progress render
    if (busy && job) {
        const total = job.total || 1;
        const cur = Math.min(job.current || 0, total);
        const pct = job.status === "queued" ? 5 : Math.round((cur / total) * 90 + 5);
        return (
            <div
                className="rounded-lg border border-primary/30 bg-primary/5 p-4"
                data-testid="video-render-in-progress"
            >
                <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="h-4 w-4 text-primary animate-spin" />
                    <span className="text-sm font-medium text-primary">
                        {STEP_LABELS[job.step] || "Rendering…"}
                    </span>
                    <span className="text-xs text-muted-foreground ml-auto">
                        {cur}/{total}
                    </span>
                </div>
                <Progress value={pct} className="h-1.5" />
                <p className="text-[11px] text-muted-foreground mt-2">
                    Slides are being narrated and stitched into an MP4. This can take 10-40
                    seconds per slide depending on length.
                </p>
                <p className="text-[11px] text-primary/80 mt-1.5 flex items-center gap-1">
                    <Film className="h-3 w-3" />
                    You can close this dialog and start rendering another lesson —
                    progress continues in the bottom-right tray.
                </p>
            </div>
        );
    }

    // Completed video
    if (fullVideoUrl) {
        return (
            <div
                className="rounded-lg border border-border bg-card p-3 space-y-2"
                data-testid="video-render-ready"
            >
                <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">
                        Lesson video ready
                    </span>
                    <span className="text-[11px] text-muted-foreground ml-auto">
                        Students see this in the learn view
                    </span>
                </div>
                <video
                    src={fullVideoUrl}
                    controls
                    preload="metadata"
                    className="w-full rounded-md aspect-video bg-black"
                    data-testid="lesson-video-preview"
                />
                <div className="flex flex-wrap items-center gap-2 justify-end pt-1">
                    <VoicePicker
                        voice={voice}
                        onChange={setVoice}
                        disabled={busy}
                        compact
                    />
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={removeVideo}
                        className="text-destructive border-destructive/30 hover:bg-destructive/5 gap-1.5"
                        data-testid="video-render-delete-btn"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Remove
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={startRender}
                        disabled={!hasStoryboard || busy}
                        className="gap-1.5"
                        data-testid="video-render-redo-btn"
                        title={`Re-render with ${VOICE_OPTIONS.find((v) => v.value === voice)?.label || voice}`}
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Re-render
                    </Button>
                </div>
            </div>
        );
    }

    // Has storyboard but no video — show the big CTA
    return (
        <div
            className="rounded-lg border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3"
            data-testid="video-render-cta"
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <Film className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">
                        Render storyboard to MP4
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        Branded slides + AI narration, stitched into a downloadable video.
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <VoicePicker voice={voice} onChange={setVoice} disabled={busy} />
                <Button
                    size="sm"
                    onClick={startRender}
                    className="bg-primary hover:bg-primary/90 gap-1.5"
                    data-testid="video-render-start-btn"
                >
                    <Play className="h-3.5 w-3.5" />
                    Render video
                </Button>
            </div>
        </div>
    );
}

/**
 * Shadcn Select wrapping the OpenAI TTS voice list. `compact` mode shrinks
 * the trigger so it fits neatly next to the existing action buttons on the
 * "video already rendered" toolbar without breaking layout on narrow dialogs.
 */
function VoicePicker({ voice, onChange, disabled, compact = false }) {
    const triggerCls = compact
        ? "h-8 w-[140px] text-xs gap-1.5"
        : "h-9 w-[160px] text-sm gap-1.5";
    return (
        <Select value={voice} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger
                className={triggerCls}
                data-testid="video-voice-picker"
                aria-label="Narration voice"
            >
                <SelectValue placeholder="Voice" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
                {VOICE_OPTIONS.map((v) => (
                    <SelectItem
                        key={v.value}
                        value={v.value}
                        data-testid={`voice-option-${v.value}`}
                    >
                        <span className="font-medium">{v.label}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                            {v.feel}
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
